import type { DeskFeature, EditorFeature } from '../models/editor-feature';
import type { FloorPlanRoomOption } from '../models/floorplan-room';

/**
 * What deleting the selection would do to a room's capacity.
 *
 * Capacity is what room-level booking counts, and it is set independently of how many desks
 * a room has — so removing desks without lowering it leaves a room still offering seats that
 * are drawn nowhere. The server reduces it on save; this works out the same numbers up front
 * so the admin is told before the desks go rather than after.
 */
export interface RoomCapacityImpact {
  readonly roomId: string;
  readonly roomName: string;
  readonly deskCount: number;
  readonly capacityBefore: number;
  readonly capacityAfter: number;
}

/**
 * What the unsaved edits have already done to each room's capacity, as a signed count.
 *
 * The room capacities on screen come from the server and are only re-read on save, so they
 * describe the plan as it was loaded. Without this, deleting two desks from a room twice in
 * one session showed "10 → 8" both times, while the save would land on 6.
 *
 * Mirrors what the server does on save: a desk added to a room is a seat gained, one removed
 * is a seat lost, and one moved between rooms is both.
 */
export const pendingCapacityDeltas = (
  loadedDesks: readonly DeskFeature[],
  currentDesks: readonly DeskFeature[],
  bookableFeatureIds: ReadonlySet<string>,
): Map<string, number> => {
  const deltas = new Map<string, number>();
  const add = (roomId: string | null, delta: number): void => {
    if (roomId) {
      deltas.set(roomId, (deltas.get(roomId) ?? 0) + delta);
    }
  };

  const loadedByKey = new Map(loadedDesks.map((desk) => [desk.featureId, desk]));
  const currentByKey = new Map(currentDesks.map((desk) => [desk.featureId, desk]));

  currentDesks.forEach((desk) => {
    const loaded = loadedByKey.get(desk.featureId);

    if (!loaded) {
      // Only a desk with a workplace behind it can be a *re-add*; a genuinely new desk has no
      // row yet, and the server charges its room a seat when it creates one.
      add(desk.roomId, 1);
      return;
    }

    if (loaded.roomId !== desk.roomId) {
      add(desk.roomId, 1);
      add(loaded.roomId, -1);
    }
  });

  loadedDesks.forEach((desk) => {
    if (!currentByKey.has(desk.featureId) && bookableFeatureIds.has(desk.featureId)) {
      add(desk.roomId, -1);
    }
  });

  return deltas;
};

interface Options {
  readonly features: readonly EditorFeature[];
  readonly rooms: readonly FloorPlanRoomOption[];
  /**
   * The desks that have a workplace behind them. Only those cost a room a seat: a desk placed
   * a moment ago and deleted again never reached the database, and 35 stored desk features
   * have no workplace at all.
   */
  readonly bookableFeatureIds: ReadonlySet<string>;
  /** Capacity already spent or reclaimed by edits not yet saved, from `pendingCapacityDeltas`. */
  readonly pendingDeltas?: ReadonlyMap<string, number>;
}

export const capacityImpactOf = ({
  features,
  rooms,
  bookableFeatureIds,
  pendingDeltas,
}: Options): RoomCapacityImpact[] => {
  const roomsById = new Map(rooms.map((room) => [room.id, room]));
  const deskCounts = new Map<string, number>();

  features.forEach((feature) => {
    if (feature.kind !== 'desk' || !feature.roomId || !bookableFeatureIds.has(feature.featureId)) {
      return;
    }

    deskCounts.set(feature.roomId, (deskCounts.get(feature.roomId) ?? 0) + 1);
  });

  return Array.from(deskCounts.entries())
    .map(([roomId, deskCount]) => {
      const room = roomsById.get(roomId);

      if (!room) {
        // A room the editor has no record of — deleted elsewhere, or belonging to another
        // office, as 113 stored desks do. Nothing can be said about its capacity, so nothing is.
        return undefined;
      }

      // Where the room's capacity stands once the edits already made are saved, which is what
      // this deletion will actually be subtracted from.
      const capacityBefore = Math.max(0, room.capacity + (pendingDeltas?.get(roomId) ?? 0));

      return {
        roomId,
        roomName: room.name,
        deskCount,
        capacityBefore,
        capacityAfter: Math.max(0, capacityBefore - deskCount),
      };
    })
    .filter((impact): impact is RoomCapacityImpact => impact !== undefined)
    .sort((first, second) => first.roomName.localeCompare(second.roomName));
};

export default capacityImpactOf;
